/**
 * The pure helpers of the page "Learn from sales": what the address says (the tab, the
 * filters, the page, the open inquiry), the queries of the four reads, and the words and
 * tones of each key of the API. No React and no request, so
 * `tests/learn-from-sales.test.mjs` checks them directly.
 *
 * The address holds every filter, so a link shows exactly what was seen:
 * `?tab=inquiries&area=dk&days=90&source=fo_thread&order=1&agreement=none&learn=conflict
 * &open=1&sort=agreement_asc&taker=T-0001&who=Alex%20Example&page=2&case=RFQ-0001`, and on
 * the tab "Learnings" `lstatus` and `lkind`. An unknown value is left out, never sent.
 */

import type {
  LineDiffCounts,
  LineDiffRow,
  SalesTruthInquiry,
  SalesTruthQuery,
  SalesTruthSummaryQuery,
  SalesTruthTrailQuery,
  TruthLearning,
  TruthLearningCounts,
  TruthLearningQuery,
} from "@/lib/api";

import {
  AGREEMENT_LABELS,
  CELL_LABELS,
  CERTAINTY_LABELS,
  CUSTOMER_MATCH_LABELS,
  LEARNING_FAILURE_LABELS,
  LEARNING_KIND_LABELS,
  LEARNING_STATUS_HINTS,
  LEARNING_STATUS_LABELS,
  LFS_TABS,
  LFS_WINDOWS,
  type LfsTab,
  type LfsWindow,
  ORDER_LINE_LABELS,
  PROPOSED_PAIR_HINT,
  QUOTE_STATUS_LABELS,
  SORT_LABELS,
  SOURCE_LABELS,
  VERDICT_LABELS,
} from "./labels";
import { TRAIL_FILTERS, type TrailFilter } from "./trail";

export type SalesTruthSource = SalesTruthInquiry["source"];
export type SalesTruthCertainty = SalesTruthInquiry["certainty"];
export type SalesTruthCustomerMatch = NonNullable<SalesTruthInquiry["customer_match"]>;
export type SalesTruthQuoteStatus = NonNullable<SalesTruthInquiry["quote_status"]>;
export type LineDiffVerdict = LineDiffRow["verdict"];
export type LearningStatus = TruthLearning["status"];
export type LearningKind = TruthLearning["kind"];
export type AgreementBand = keyof typeof AGREEMENT_LABELS;
export type LfsSort = keyof typeof SORT_LABELS;

/** The link sources in the order of the API enum. */
export const LFS_SOURCES = Object.keys(SOURCE_LABELS) as SalesTruthSource[];
export const LFS_AGREEMENTS = Object.keys(AGREEMENT_LABELS) as AgreementBand[];
export const LFS_SORTS = Object.keys(SORT_LABELS) as LfsSort[];
export const LEARNING_STATUSES = Object.keys(LEARNING_STATUS_LABELS) as LearningStatus[];
export const LEARNING_KINDS = Object.keys(LEARNING_KIND_LABELS) as LearningKind[];
/** The verdicts in the order the table and the overview list them. */
export const DIFF_VERDICTS = Object.keys(VERDICT_LABELS) as LineDiffVerdict[];
/** The verdicts that say where I2Q differs from sales (the overview leaves out "Same item"). */
export const DIFFERENCE_VERDICTS: readonly LineDiffVerdict[] = [
  "missed",
  "other_item",
  "extra",
  "qty_differs",
  "price_differs",
  "not_quoted",
];

/**
 * The statuses a list row counts as "learnings" (`sales_truth.common.COUNTED_STATUSES`):
 * what I2Q learns, not what it confirms, and not a conflict (two quotations disagree, so
 * nothing is applied: counted apart).
 */
export const COUNTED_STATUSES: readonly LearningStatus[] = ["observed", "proposed", "active"];
/** The default list of the tab "Learnings": the learnings and the conflicts. */
export const LISTED_STATUSES: readonly LearningStatus[] = [...COUNTED_STATUSES, "conflict"];
/** A manager may approve these (`APPROVABLE_STATUSES`). */
export const APPROVABLE_STATUSES: readonly LearningStatus[] = ["observed", "proposed"];
/** A manager may retract these (`RETRACTABLE_STATUSES`). */
export const RETRACTABLE_STATUSES: readonly LearningStatus[] = [
  "observed",
  "proposed",
  "active",
  "conflict",
];

/** The capability that approves and retracts a learning (managers). */
export const APPROVE_CAPABILITY = "approve_learned";

/** The rows of one page of the tab "Inquiries" and the tab "Learnings". */
export const PAGE_SIZE = 50;
export const DEFAULT_DAYS: LfsWindow = 30;

// ─── the address ──────────────────────────────────────────────────────────────

export interface LfsView {
  tab: LfsTab;
  /** Entity codes (`dk`, `wgg`), sorted; empty: every area of the session. */
  areas: string[];
  days: LfsWindow;
  source?: SalesTruthSource;
  hasOrder?: boolean;
  agreement?: AgreementBand;
  learn?: LearningStatus;
  openInI2q?: boolean;
  sort: LfsSort;
  /** The sales person of the filter (`sales_person_id`) and the name to print. */
  taker?: string;
  takerName?: string;
  /** The page of the tab "Inquiries", from 1. */
  page: number;
  /** The filters and the page of the tab "Learnings". */
  learnStatus?: LearningStatus;
  learnKind?: LearningKind;
  learnPage: number;
  /** The inquiry whose drawer is open. */
  caseId: string | null;
  /** The tab "Email to order": the area tab (`undefined`: the overview) and the chip. */
  trailArea?: string;
  trailTag?: TrailFilter;
}

const ENTITY = /^[a-z]{2,4}$/;
const TAKER = /^[\w.@ -]{1,60}$/;
const CASE_ID = /^[\w.:@-]{1,120}$/;

function pick<T extends string>(value: string | null, allowed: readonly T[]): T | undefined {
  return value !== null && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

function pageOf(value: string | null): number {
  const page = Number(value);
  return Number.isInteger(page) && page >= 1 && page <= 10_000 ? page : 1;
}

/** The view of the address; an unknown value falls back to the default. */
export function toView(params: URLSearchParams): LfsView {
  const areas = [
    ...new Set(
      params
        .getAll("area")
        .flatMap((value) => value.split(","))
        .map((value) => value.trim().toLowerCase())
        .filter((value) => ENTITY.test(value)),
    ),
  ].sort();
  const days = Number(params.get("days"));
  const order = params.get("order");
  const taker = (params.get("taker") ?? "").trim();
  const who = (params.get("who") ?? "").trim().slice(0, 80);
  const caseId = (params.get("case") ?? "").trim();
  const trailArea = (params.get("ta") ?? "").trim().toLowerCase();
  return {
    tab: pick(params.get("tab"), LFS_TABS) ?? "overview",
    areas,
    days: (LFS_WINDOWS as readonly number[]).includes(days) ? (days as LfsWindow) : DEFAULT_DAYS,
    source: pick(params.get("source"), LFS_SOURCES),
    hasOrder: order === "1" ? true : order === "0" ? false : undefined,
    agreement: pick(params.get("agreement"), LFS_AGREEMENTS),
    learn: pick(params.get("learn"), LEARNING_STATUSES),
    openInI2q: params.get("open") === "1" ? true : undefined,
    sort: pick(params.get("sort"), LFS_SORTS) ?? "received_desc",
    taker: TAKER.test(taker) ? taker : undefined,
    takerName: TAKER.test(taker) && who !== "" ? who : undefined,
    page: pageOf(params.get("page")),
    learnStatus: pick(params.get("lstatus"), LEARNING_STATUSES),
    learnKind: pick(params.get("lkind"), LEARNING_KINDS),
    learnPage: pageOf(params.get("lpage")),
    caseId: CASE_ID.test(caseId) ? caseId : null,
    trailArea: ENTITY.test(trailArea) ? trailArea : undefined,
    trailTag: pick(params.get("tt"), TRAIL_FILTERS),
  };
}

/** The address of a view: the reverse of `toView`; defaults are left out. */
export function toParams(view: LfsView): URLSearchParams {
  const params = new URLSearchParams();
  if (view.tab !== "overview") {
    params.set("tab", view.tab);
  }
  for (const area of view.areas) {
    params.append("area", area);
  }
  if (view.days !== DEFAULT_DAYS) {
    params.set("days", String(view.days));
  }
  if (view.source) {
    params.set("source", view.source);
  }
  if (view.hasOrder !== undefined) {
    params.set("order", view.hasOrder ? "1" : "0");
  }
  if (view.agreement) {
    params.set("agreement", view.agreement);
  }
  if (view.learn) {
    params.set("learn", view.learn);
  }
  if (view.openInI2q) {
    params.set("open", "1");
  }
  if (view.sort !== "received_desc") {
    params.set("sort", view.sort);
  }
  if (view.taker) {
    params.set("taker", view.taker);
    if (view.takerName) {
      params.set("who", view.takerName);
    }
  }
  if (view.page > 1) {
    params.set("page", String(view.page));
  }
  if (view.learnStatus) {
    params.set("lstatus", view.learnStatus);
  }
  if (view.learnKind) {
    params.set("lkind", view.learnKind);
  }
  if (view.learnPage > 1) {
    params.set("lpage", String(view.learnPage));
  }
  if (view.caseId) {
    params.set("case", view.caseId);
  }
  if (view.trailArea) {
    params.set("ta", view.trailArea);
  }
  if (view.trailTag) {
    params.set("tt", view.trailTag);
  }
  return params;
}

/** A change of a filter starts both lists on their first page again. */
export function withFilters(view: LfsView, change: Partial<LfsView>): LfsView {
  return { ...view, page: 1, learnPage: 1, ...change };
}

// ─── the queries ──────────────────────────────────────────────────────────────

/**
 * The query of the tab "Overview" (and the shared part of the tab "Inquiries"). Never the
 * sales person: the overview divides by every inquiry of the period, so a person filter
 * would turn "Linked" into a share per person (S3: no score of a person).
 */
export function summaryQuery(view: LfsView): SalesTruthSummaryQuery {
  const query: SalesTruthSummaryQuery = { days: view.days };
  if (view.areas.length > 0) {
    query.area = view.areas;
  }
  if (view.source) {
    query.source = [view.source];
  }
  return query;
}

/** The query of one page of the tab "Inquiries". */
export function inquiriesQuery(view: LfsView): SalesTruthQuery {
  const query: SalesTruthQuery = {
    ...summaryQuery(view),
    sort: view.sort,
    limit: PAGE_SIZE,
    offset: (view.page - 1) * PAGE_SIZE,
  };
  if (view.taker) {
    query.taker = view.taker;
  }
  if (view.hasOrder !== undefined) {
    query.has_order = view.hasOrder;
  }
  if (view.agreement) {
    query.agreement = view.agreement;
  }
  if (view.learn) {
    query.learn_status = view.learn;
  }
  if (view.openInI2q) {
    query.open_in_i2q = true;
  }
  return query;
}

/** The query of the tab "Email to order": the areas and the period only. */
export function trailQuery(view: LfsView): SalesTruthTrailQuery {
  const query: SalesTruthTrailQuery = { days: view.days };
  if (view.areas.length > 0) {
    query.area = view.areas;
  }
  return query;
}

/** The query of the tab "Sales people": the areas and the period only. */
export function peopleQuery(view: LfsView): SalesTruthSummaryQuery {
  const query: SalesTruthSummaryQuery = { days: view.days };
  if (view.areas.length > 0) {
    query.area = view.areas;
  }
  return query;
}

/** The query of one page of the tab "Learnings"; no status: the counted ones. */
export function learningsQuery(view: LfsView): TruthLearningQuery {
  const query: TruthLearningQuery = {
    days: view.days,
    status: view.learnStatus ? [view.learnStatus] : [...LISTED_STATUSES],
    limit: PAGE_SIZE,
    offset: (view.learnPage - 1) * PAGE_SIZE,
  };
  if (view.areas.length > 0) {
    query.area = view.areas;
  }
  if (view.learnKind) {
    query.kind = view.learnKind;
  }
  if (view.taker) {
    query.taker = view.taker;
  }
  return query;
}

// ─── the words and the tones ──────────────────────────────────────────────────

function labelOf(labels: Record<string, string>, key: string | null | undefined): string {
  if (!key) {
    return CELL_LABELS.notKnown;
  }
  return labels[key] ?? key;
}

export const sourceLabel = (source: string) => labelOf(SOURCE_LABELS, source);
export const certaintyLabel = (certainty: string) => labelOf(CERTAINTY_LABELS, certainty);
export const quoteStatusLabel = (status: string | null | undefined) =>
  labelOf(QUOTE_STATUS_LABELS, status);
export const verdictLabel = (verdict: string) => labelOf(VERDICT_LABELS, verdict);
export const learningStatusLabel = (status: string) => labelOf(LEARNING_STATUS_LABELS, status);
export const learningKindLabel = (kind: string) => labelOf(LEARNING_KIND_LABELS, kind);
export const customerMatchLabel = (match: string | null | undefined) =>
  labelOf(CUSTOMER_MATCH_LABELS, match ?? "no_fo");

export type LfsTone = "neutral" | "info" | "success" | "warning" | "accent";

/**
 * The tone of a certainty: information for "Exact", neutral for the others. A link is
 * never "good" or "bad", so it is never green or red (design 2.3).
 */
export function certaintyTone(certainty: string): LfsTone {
  return certainty === "exact" ? "info" : "neutral";
}

/** Won is a success, lost and open are neutral (a lost quotation is no fault of I2Q). */
export function quoteStatusTone(status: string | null | undefined): LfsTone {
  return status === "won" ? "success" : "neutral";
}

/** The badge of the state of a quoted line on the order (round 3 words and round 4 words). */
export interface OrderStateBadge {
  label: string;
  hint: string;
  tone: LfsTone;
  /** `data-ordered`: "yes" for an ordered line, else "no". */
  mark: "yes" | "no";
  state: string;
}

/**
 * The badge of a quoted line on the order (pure). `state` comes from the server
 * (`LineDiffRow.order_state`): "ordered", "removed", "cancelled" or "not_counted" (on the
 * order with quantity 0 or without an order status: "Not ordered") with the lines of the sales
 * order, "quoted_ordered" or "not_ordered" with the quotation lines at the order. Without a
 * state the mark of round 3 (`ordered`) gives the quotation words. `null`: no order lines.
 */
export function orderStateBadge(
  ordered: boolean | null | undefined,
  state?: string | null,
): OrderStateBadge | null {
  const fallback =
    typeof ordered === "boolean" ? (ordered ? "quoted_ordered" : "not_ordered") : null;
  switch (state ?? fallback) {
    case "ordered":
      return {
        label: ORDER_LINE_LABELS.ordered,
        hint: ORDER_LINE_LABELS.orderedHint,
        tone: "info",
        mark: "yes",
        state: "ordered",
      };
    case "quoted_ordered":
      return {
        label: ORDER_LINE_LABELS.ordered,
        hint: ORDER_LINE_LABELS.quoteOrderedHint,
        tone: "info",
        mark: "yes",
        state: "ordered",
      };
    case "not_ordered":
      return {
        label: ORDER_LINE_LABELS.notOrdered,
        hint: ORDER_LINE_LABELS.notOrderedHint,
        tone: "neutral",
        mark: "no",
        state: "not_ordered",
      };
    case "not_counted":
      return {
        label: ORDER_LINE_LABELS.notOrdered,
        hint: ORDER_LINE_LABELS.notCountedHint,
        tone: "neutral",
        mark: "no",
        state: "not_counted",
      };
    case "removed":
      return {
        label: ORDER_LINE_LABELS.removed,
        hint: ORDER_LINE_LABELS.removedHint,
        tone: "warning",
        mark: "no",
        state: "removed",
      };
    case "cancelled":
      return {
        label: ORDER_LINE_LABELS.cancelled,
        hint: ORDER_LINE_LABELS.cancelledHint,
        tone: "warning",
        mark: "no",
        state: "cancelled",
      };
    default:
      return null;
  }
}

/** Same item green; a difference amber; a missed or extra line amber; not quoted neutral. */
export function verdictTone(verdict: string): LfsTone {
  switch (verdict) {
    case "same_item":
      return "success";
    case "not_quoted":
      return "neutral";
    default:
      return "warning";
  }
}

/**
 * Active and confirmed are a success, conflict a warning, observed information. Proposed
 * (written, a manager approves it) has the accent, so it stands apart from observed once
 * `truth_learn_apply` is ON (20 Oct 2026).
 */
export function learningStatusTone(status: string): LfsTone {
  switch (status) {
    case "active":
    case "confirmed":
      return "success";
    case "conflict":
      return "warning";
    case "observed":
      return "info";
    case "proposed":
      return "accent";
    default:
      return "neutral";
  }
}

/**
 * What a status means, as the title of its badge. A proposed customer learning has its
 * pair in use at once (G4), so its hint says so (finding R7 of 20 Oct 2026). An item that
 * decision D6 made active (one quotation whose order has the line) says so too, and an item
 * that the take-back of D6 set back to proposed (round 4) gives its note.
 */
export function learningStatusHint(
  status: string,
  kind: string,
  note?: string | null,
): string | undefined {
  if (status === "proposed" && kind === "customer") {
    return PROPOSED_PAIR_HINT;
  }
  if (status === "active" && kind === "item" && note === ORDER_LINE_LABELS.activeNote) {
    return ORDER_LINE_LABELS.activeHint;
  }
  if (status === "active" && kind === "item" && note === ORDER_LINE_LABELS.activeNoteSalesOrder) {
    return ORDER_LINE_LABELS.activeHintSalesOrder;
  }
  if (status === "proposed" && kind === "item" && note === ORDER_LINE_LABELS.takeBackNote) {
    return ORDER_LINE_LABELS.takeBackNote;
  }
  return (LEARNING_STATUS_HINTS as Record<string, string>)[status];
}

/** The sales person of a row: "Not shown" for Germany and France (S3), else the name. */
export function personText(row: {
  sales_person?: string | null;
  person_hidden?: boolean | null;
}): string {
  if (row.person_hidden) {
    return CELL_LABELS.notShown;
  }
  const name = row.sales_person?.trim();
  return name ? name : CELL_LABELS.notKnown;
}

/** "57%" for a share; "Not known" without one. */
export function shareText(share: number | null | undefined): string {
  if (typeof share !== "number" || !Number.isFinite(share)) {
    return CELL_LABELS.notKnown;
  }
  return `${Math.round(Math.max(0, Math.min(1, share)) * 100)}%`;
}

/** The line agreement as a whole per cent for a meter, 0 without a value. */
export function agreementPercent(share: number | null | undefined): number {
  return typeof share === "number" && Number.isFinite(share)
    ? Math.round(Math.max(0, Math.min(1, share)) * 100)
    : 0;
}

/** The learnings of the counts that I2Q learns (not confirmed, conflict, unaligned or retracted). */
export function countedLearnings(counts: TruthLearningCounts | null | undefined): number {
  if (!counts) {
    return 0;
  }
  return COUNTED_STATUSES.reduce((sum, status) => sum + (counts[status] ?? 0), 0);
}

/** The rows of a line difference count by verdict, in the order of `DIFF_VERDICTS`. */
export function verdictCounts(
  counts: LineDiffCounts | null | undefined,
  verdicts: readonly LineDiffVerdict[] = DIFF_VERDICTS,
): { verdict: LineDiffVerdict; count: number }[] {
  return verdicts
    .map((verdict) => ({ verdict, count: counts?.[verdict] ?? 0 }))
    .filter((entry) => entry.count > 0);
}

/**
 * True when "Approve" can work: the switch `truth_learn_apply` is ON, the status allows
 * it, the learning is not observed only, an item learning has a customer account, and the
 * sender of a customer learning has no multi-account mark (decision D5). The server
 * refuses every other case with 409, so the button is not offered.
 */
export function canApprove(
  learning: Pick<TruthLearning, "status" | "observe_only" | "kind" | "customer_account"> & {
    sender_multi_account?: boolean | null;
  },
  applyOn: boolean,
): boolean {
  if (!applyOn || !APPROVABLE_STATUSES.includes(learning.status) || learning.observe_only) {
    return false;
  }
  if (learning.kind === "customer") {
    return learning.sender_multi_account !== true;
  }
  return Boolean(learning.customer_account?.trim());
}

/** True when a manager can set or clear the multi-account mark: a customer learning (D5). */
export function canMark(learning: Pick<TruthLearning, "kind" | "inquiry_text">): boolean {
  return learning.kind === "customer" && Boolean(learning.inquiry_text?.trim());
}

/** The statuses of the counts row (the counts also carry the marked senders). */
export function statusCounts(
  counts: Partial<Record<LearningStatus, number>>,
  picked: LearningStatus | undefined,
): LearningStatus[] {
  return LEARNING_STATUSES.filter((status) => (counts[status] ?? 0) > 0 || status === picked);
}

export function canRetract(status: LearningStatus): boolean {
  return RETRACTABLE_STATUSES.includes(status);
}

/** Why a decision did not save, by the status of the error and the reason of a 409. */
export function learningFailureText(error: unknown): string {
  const status = (error as { status?: number } | null)?.status;
  const body = (error as { body?: { detail?: unknown } } | null)?.body;
  const detail = typeof body?.detail === "string" ? body.detail : "";
  switch (status) {
    case 409:
      if (detail.includes("truth_learn_apply")) {
        return LEARNING_FAILURE_LABELS.applyOff;
      }
      if (detail.includes("observed only")) {
        return LEARNING_FAILURE_LABELS.observeOnly;
      }
      if (detail.includes("multi-account sender")) {
        return LEARNING_FAILURE_LABELS.multiAccount;
      }
      return LEARNING_FAILURE_LABELS.changed;
    case 403:
      return LEARNING_FAILURE_LABELS.noRight;
    case 404:
      return LEARNING_FAILURE_LABELS.gone;
    default:
      return LEARNING_FAILURE_LABELS.other;
  }
}

/** "Name (account)" for a customer; the account alone without a name; "None" without both. */
export function customerText(
  account: string | null | undefined,
  name?: string | null | undefined,
): string {
  const number = account?.trim();
  const label = name?.trim();
  if (number && label) {
    return `${label} (${number})`;
  }
  return number || label || CELL_LABELS.none;
}

/** "0990001, qty 2, 41.50" for one side of a diff row; "None" without an item. */
export function sideText(
  item: string | null | undefined,
  qty: number | null | undefined,
  price: number | null | undefined,
): string {
  if (!item) {
    return CELL_LABELS.none;
  }
  const parts = [item];
  if (typeof qty === "number" && Number.isFinite(qty)) {
    parts.push(`qty ${formatNumber(qty)}`);
  }
  if (typeof price === "number" && Number.isFinite(price)) {
    parts.push(formatNumber(price, 2));
  }
  return parts.join(", ");
}

function formatNumber(value: number, digits = 0): string {
  return new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : digits,
    maximumFractionDigits: Math.max(digits, 3),
  }).format(value);
}

/** The day of an ISO time ("2026-10-14"); empty without one. */
export function dayOf(iso: string | null | undefined): string {
  return typeof iso === "string" && iso.length >= 10 ? iso.slice(0, 10) : "";
}
